import {
	TypographyCaps,
	TypographyLabel,
	TypographyParagraph,
} from '../typography'
import clsx from 'clsx'
import { useDropzone } from 'react-dropzone-esm'
import { useEffect, useState } from 'react'
import UploadImageIcon from '../icons/Actions/UploadImageIcon'
import UploadArrow from '../icons/Arrows/UploadArrow'
import { ArrowRightIcon } from '../icons'
import ContentTile from '../content/ContentTile'

const PreviewPinImageInputField = ({
	aspectRatio = 1,
	isValid,
	isError,
	isInactive = false,
	icon,
	containerWidth = 'w-full',
	value = undefined,
	label,
	helper,
	onChange = () => {},
	onDelete = () => {},
	currentFile,
	isUploading,
}: {
	aspectRatio?: number
	isValid?: boolean
	isError?: boolean
	isInactive?: boolean
	icon?: React.ReactNode
	containerWidth?: string
	value?: string
	label: string
	helper: string
	onChange?: (e: File) => void
	onDelete?: () => void
	currentFile?: File | undefined
	isUploading: boolean
}) => {
	const [imageToRender, setImageToRender] = useState(
		value && value !== undefined && value !== '' ? value : undefined,
	)

	const handleOnChange = (e: File) => {
		onChange(e)
	}

	const handleOnDelete = () => {
		setImageToRender(undefined)
		onDelete()
	}

	useEffect(() => {
		if (imageToRender === undefined && value !== undefined && value !== '') {
			setImageToRender(value)
		}
	}, [value, imageToRender])

	const { getRootProps, getInputProps, open } = useDropzone({
		accept: {
			'image/jpeg': [],
			'image/png': [],
			'image/gif': [],
			'image/svg': [],
		},
		noClick: true,
		onDrop: acceptedFiles => {
			if (acceptedFiles.length) {
				const fileToRender = acceptedFiles[acceptedFiles.length - 1]
				setImageToRender(getURLForFile(fileToRender))
				handleOnChange(fileToRender)
			}
		},
	})

	const getURLForFile = (file: File) => {
		return URL.createObjectURL(file)
	}

	const bytesToMB = (bytes: number) => {
		const megabyte = 1024 * 1024 // 1MB = 1024 * 1024 bytes
		const result = bytes / megabyte
		return result.toFixed(4)
	}

	return (
		<div
			className={clsx(
				'rounded-lg border border-uiSecondaryLight p-4 dark:border-uiSecondaryDark',
				containerWidth,
			)}
		>
			<TypographyLabel
				size="medium"
				additionalClasses="dark:text-light text-dark mb-2"
			>
				{label}
			</TypographyLabel>
			<TypographyParagraph
				size="small"
				additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
			>
				{helper}
			</TypographyParagraph>
			<div
				{...getRootProps({
					className: clsx(
						'dropzone group relative mt-2 w-full cursor-pointer flex hover:transition-all ease-in-out hover:duration-300',
					),
				})}
			>
				<div
					className={clsx(
						'container m-2 flex h-6',
						isError ? 'border-red' : '',
					)}
				>
					<input {...getInputProps()} />
					{isUploading ? (
						<div className="flex flex-col items-start justify-center">
							<div className="flex items-center justify-center text-textPrimaryLight dark:text-textPrimaryDark">
								<ArrowRightIcon />
								<TypographyLabel
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
									isBold={true}
									size="medium"
								>
									Uploading Screenshot
								</TypographyLabel>
							</div>
						</div>
					) : (
						<div
							onClick={() => open()}
							className="flex flex-col items-start justify-center"
						>
							<div className="flex items-center justify-center text-textPrimaryLight dark:text-textPrimaryDark">
								<ArrowRightIcon />
								<TypographyLabel
									additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
									isBold={true}
									size="medium"
								>
									Upload Screenshot
								</TypographyLabel>
							</div>
						</div>
					)}
				</div>
				<div
					className={clsx(
						'pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3',
						isValid &&
							'text-green group-focus:text-textPrimaryLight dark:group-focus:text-textPrimaryDark',
						isError &&
							'text-red group-focus:text-textPrimaryLight dark:group-focus:text-textPrimaryDark',
						isInactive && 'text-textInactiveLight dark:text-textInactiveDark',
					)}
				>
					{icon}
				</div>
			</div>
			{currentFile && value && (
				<div className="h-[72px]">
					{/* TITLE: filename, description: filesize */}
					<ContentTile
						title={currentFile.name}
						image={value}
						description={
							currentFile.size ? `${bytesToMB(currentFile.size)} mb` : ''
						}
						variant="screenshotPreview"
						handleDelete={() => handleOnDelete()}
					/>
				</div>
			)}
		</div>
	)
}

export default PreviewPinImageInputField
